import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { formatDate } from '../lib/formatUtils';
import { PersonAvatar } from '../components/PersonAvatar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowLeft, Plus, Loader2, Bug, FlaskConical, ClipboardCheck, BarChart3, CheckCircle2, XCircle, AlertTriangle, Circle, Play, Trash2, Edit, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirmation } from '../components/ConfirmationProvider';

const priorityColors = { high: 'bg-red-500/10 text-red-500 border-red-500/20', medium: 'bg-amber-500/10 text-amber-500 border-amber-500/20', low: 'bg-blue-500/10 text-blue-500 border-blue-500/20' };
const statusColors = { pass: 'bg-emerald-500/10 text-emerald-500', fail: 'bg-red-500/10 text-red-500', blocked: 'bg-amber-500/10 text-amber-500', not_run: 'bg-zinc-500/10 text-zinc-400' };
const bugStatusColors = { open: 'bg-red-500/10 text-red-500', assigned: 'bg-amber-500/10 text-amber-500', fixed: 'bg-blue-500/10 text-blue-500', retest: 'bg-purple-500/10 text-purple-500', closed: 'bg-emerald-500/10 text-emerald-500' };

export const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirmation();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mainTab, setMainTab] = useState('qa');
  const [qaTab, setQaTab] = useState('dashboard');

  // QA State
  const [testCases, setTestCases] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [bugs, setBugs] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);

  // Dialogs
  const [caseDialog, setCaseDialog] = useState(false);
  const [sessionDialog, setSessionDialog] = useState(false);
  const [bugDialog, setBugDialog] = useState(false);
  const [sessionDetailId, setSessionDetailId] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [addCasesDialog, setAddCasesDialog] = useState(false);
  const [selectedCaseIds, setSelectedCaseIds] = useState([]);
  const [checklistDialog, setChecklistDialog] = useState(false);
  const [members, setMembers] = useState([]);
  const [addMemberDialog, setAddMemberDialog] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);

  // Forms
  const [caseForm, setCaseForm] = useState({ title: '', module: '', steps: '', expected_result: '', priority: 'medium', is_critical: false });
  const [editCaseId, setEditCaseId] = useState(null);
  const [sessionForm, setSessionForm] = useState({ name: '', session_type: 'pre_release', build_version: '', build_source: '' });
  const [bugForm, setBugForm] = useState({ title: '', description: '', priority: 'medium', found_in: '', assigned_to: '', test_case_id: '', session_id: '', task_id: '' });
  const [checklistForm, setChecklistForm] = useState({ title: '', items: '' });

  const fetchProject = useCallback(async () => {
    try {
      const res = await api.get(`/projects/${id}`);
      if (res.data.success) setProject(res.data.data);
    } catch { toast.error('Failed to load project'); }
    finally { setLoading(false); }
  }, [id]);

  const fetchTestCases = useCallback(async () => {
    try { const res = await api.get(`/qa/test-cases?project_id=${id}`); if (res.data.success) setTestCases(res.data.data); } catch {}
  }, [id]);

  const fetchSessions = useCallback(async () => {
    try { const res = await api.get(`/qa/sessions?project_id=${id}`); if (res.data.success) setSessions(res.data.data); } catch {}
  }, [id]);

  const fetchBugs = useCallback(async () => {
    try { const res = await api.get(`/qa/bugs?project_id=${id}`); if (res.data.success) setBugs(res.data.data); } catch {}
  }, [id]);

  const fetchDashboard = useCallback(async () => {
    try { const res = await api.get(`/qa/dashboard?project_id=${id}`); if (res.data.success) setDashboard(res.data.data); } catch {}
  }, [id]);

  const fetchSessionDetail = useCallback(async (sid) => {
    try { const res = await api.get(`/qa/sessions/${sid}`); if (res.data.success) setSessionDetail(res.data.data); } catch {}
  }, []);

  const fetchMembers = useCallback(async () => {
    try { const res = await api.get(`/projects/${id}/members`); if (res.data.success) setMembers(res.data.data); } catch {}
  }, [id]);

  useEffect(() => { fetchProject(); fetchMembers(); api.get('/users?status=active').then(r => { if (r.data.success) setUsers(r.data.data); }).catch(() => {}); }, [fetchProject, fetchMembers]);
  useEffect(() => { if (qaTab === 'test-cases') fetchTestCases(); if (qaTab === 'sessions') fetchSessions(); if (qaTab === 'bugs') fetchBugs(); if (qaTab === 'dashboard') { fetchDashboard(); fetchBugs(); } }, [qaTab, fetchTestCases, fetchSessions, fetchBugs, fetchDashboard]);
  useEffect(() => { if (sessionDetailId) fetchSessionDetail(sessionDetailId); }, [sessionDetailId, fetchSessionDetail]);

  // Handlers
  const handleSaveCase = async () => {
    if (!caseForm.title) { toast.error('Title required'); return; }
    try {
      if (editCaseId) { await api.put(`/qa/test-cases/${editCaseId}`, caseForm); toast.success('Test case updated'); }
      else { await api.post('/qa/test-cases', { ...caseForm, project_id: id }); toast.success('Test case created'); }
      setCaseDialog(false); setCaseForm({ title: '', module: '', steps: '', expected_result: '', priority: 'medium', is_critical: false }); setEditCaseId(null); fetchTestCases();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleSaveSession = async () => {
    if (!sessionForm.name || !sessionForm.session_type) { toast.error('Name and type required'); return; }
    try {
      await api.post('/qa/sessions', { ...sessionForm, project_id: id }); toast.success('Session created');
      setSessionDialog(false); setSessionForm({ name: '', session_type: 'pre_release', build_version: '', build_source: '' }); fetchSessions();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleSaveBug = async () => {
    if (!bugForm.title) { toast.error('Title required'); return; }
    try {
      await api.post('/qa/bugs', { ...bugForm, project_id: id }); toast.success('Bug reported');
      setBugDialog(false); setBugForm({ title: '', description: '', priority: 'medium', found_in: '', assigned_to: '', test_case_id: '', session_id: '', task_id: '' }); fetchBugs(); fetchDashboard();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleAddCasesToSession = async () => {
    if (!selectedCaseIds.length) { toast.error('Select at least one test case'); return; }
    try {
      await api.post(`/qa/sessions/${sessionDetailId}/add-cases`, { case_ids: selectedCaseIds });
      toast.success('Cases added'); setAddCasesDialog(false); setSelectedCaseIds([]); fetchSessionDetail(sessionDetailId);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleExecStatus = async (execId, status) => {
    try { await api.put(`/qa/session-cases/${execId}`, { status }); fetchSessionDetail(sessionDetailId); } catch { toast.error('Update failed'); }
  };

  const handleBugStatus = async (bugId, status) => {
    try { await api.put(`/qa/bugs/${bugId}`, { status }); fetchBugs(); fetchDashboard(); } catch { toast.error('Failed'); }
  };

  const handleSaveChecklist = async () => {
    if (!checklistForm.title) { toast.error('Title required'); return; }
    const items = checklistForm.items.split('\n').map(s => s.trim()).filter(Boolean);
    try {
      await api.post('/qa/checklists', { session_id: sessionDetailId, title: checklistForm.title, items });
      toast.success('Checklist created'); setChecklistDialog(false); setChecklistForm({ title: '', items: '' }); fetchSessionDetail(sessionDetailId);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleChecklistItemStatus = async (itemId, status) => {
    try { await api.put(`/qa/checklist-items/${itemId}`, { status }); fetchSessionDetail(sessionDetailId); } catch {}
  };

  const handleAddMembers = async () => {
    if (!selectedMemberIds.length) { toast.error('Select at least one member'); return; }
    try {
      await api.post(`/projects/${id}/members`, { user_ids: selectedMemberIds });
      toast.success('Members added'); setAddMemberDialog(false); setSelectedMemberIds([]); fetchMembers(); fetchProject();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleRemoveMember = async (userId) => {
    const ok = await confirm({ title: 'Remove Member', description: 'Remove this member from the project?' });
    if (!ok) return;
    try { await api.delete(`/projects/${id}/members/${userId}`); toast.success('Member removed'); fetchMembers(); fetchProject(); } catch { toast.error('Failed'); }
  };

  const memberIds = new Set(members.map(m => m.id));

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (!project) return <div className="text-center py-16 text-muted-foreground">Project not found</div>;

  const getUserName = (uid) => { const u = users.find(u => u.id === uid); return u ? `${u.first_name} ${u.last_name}` : ''; };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="project-detail-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/projects')} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{project.name}</h1>
          <p className="text-muted-foreground">{project.client_name}</p>
        </div>
        <Button onClick={() => setBugDialog(true)} variant="outline" className="text-red-500 border-red-500/30" data-testid="report-bug-btn"><Bug className="h-4 w-4 mr-2" />Report Bug</Button>
      </div>

      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="team" data-testid="team-tab">Team ({members.length})</TabsTrigger>
          <TabsTrigger value="qa" data-testid="qa-tab">QA Module</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card><CardContent className="pt-6"><p className="text-sm text-muted-foreground">Status</p><Badge className="mt-1 capitalize">{project.status}</Badge></CardContent></Card>
            <Card><CardContent className="pt-6"><p className="text-sm text-muted-foreground">Client</p><p className="font-medium mt-1">{project.client_name || '-'}</p></CardContent></Card>
            <Card><CardContent className="pt-6"><p className="text-sm text-muted-foreground">Start Date</p><p className="font-medium mt-1">{formatDate(project.start_date) || '-'}</p></CardContent></Card>
            <Card><CardContent className="pt-6"><p className="text-sm text-muted-foreground">Deadline</p><p className="font-medium mt-1">{formatDate(project.deadline) || '-'}</p></CardContent></Card>
          </div>
        </TabsContent>

        <TabsContent value="team" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">{members.length} member{members.length !== 1 ? 's' : ''} assigned</p>
            <Button onClick={() => { setSelectedMemberIds([]); setAddMemberDialog(true); }} data-testid="add-member-btn"><Plus className="h-4 w-4 mr-2" />Add Members</Button>
          </div>
          {members.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground"><Users className="h-10 w-10 mx-auto mb-3 opacity-40" /><p>No members assigned yet</p><p className="text-xs mt-1">Add team members to give them access to this project</p></CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {members.map(m => (
                <Card key={m.id} data-testid={`member-${m.id}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <PersonAvatar firstName={m.first_name} lastName={m.last_name} picture={m.profile_picture} seed={m.id} size="md" />
                      <div><p className="font-medium text-sm">{m.first_name} {m.last_name}</p><p className="text-xs text-muted-foreground">{m.email}</p></div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:bg-red-500/10" onClick={() => handleRemoveMember(m.id)} data-testid={`remove-member-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="qa">
          <Tabs value={qaTab} onValueChange={setQaTab}>
            <TabsList>
              <TabsTrigger value="dashboard" data-testid="qa-dashboard-tab"><BarChart3 className="h-3.5 w-3.5 mr-1.5" />Dashboard</TabsTrigger>
              <TabsTrigger value="test-cases" data-testid="qa-cases-tab"><FlaskConical className="h-3.5 w-3.5 mr-1.5" />Test Cases</TabsTrigger>
              <TabsTrigger value="sessions" data-testid="qa-sessions-tab"><Play className="h-3.5 w-3.5 mr-1.5" />Sessions</TabsTrigger>
              <TabsTrigger value="bugs" data-testid="qa-bugs-tab"><Bug className="h-3.5 w-3.5 mr-1.5" />Bugs {bugs.filter(b => b.status !== 'closed').length > 0 && <Badge className="ml-1 h-5 px-1.5 text-xs bg-red-500 text-white">{bugs.filter(b => b.status !== 'closed').length}</Badge>}</TabsTrigger>
            </TabsList>

            {/* ===== DASHBOARD ===== */}
            <TabsContent value="dashboard" className="space-y-6">
              {dashboard ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{dashboard.test_summary.total_cases}</p><p className="text-xs text-muted-foreground">Test Cases</p></CardContent></Card>
                    <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-emerald-500">{dashboard.test_summary.passed}</p><p className="text-xs text-muted-foreground">Passed</p></CardContent></Card>
                    <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-red-500">{dashboard.test_summary.failed}</p><p className="text-xs text-muted-foreground">Failed</p></CardContent></Card>
                    <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-amber-500">{dashboard.test_summary.not_run}</p><p className="text-xs text-muted-foreground">Not Run</p></CardContent></Card>
                    <Card className={`border-2 ${dashboard.release_status === 'green' ? 'border-emerald-500/50' : dashboard.release_status === 'yellow' ? 'border-amber-500/50' : 'border-red-500/50'}`}>
                      <CardContent className="p-4 text-center">
                        <div className={`inline-block h-4 w-4 rounded-full mb-1 ${dashboard.release_status === 'green' ? 'bg-emerald-500' : dashboard.release_status === 'yellow' ? 'bg-amber-500' : 'bg-red-500'}`} />
                        <p className="text-xs text-muted-foreground">{dashboard.release_status === 'green' ? 'Ready' : dashboard.release_status === 'yellow' ? 'Risk' : 'Not Ready'}</p>
                      </CardContent>
                    </Card>
                  </div>
                  {dashboard.mismatches.length > 0 && (
                    <Card className="border-red-500/30"><CardContent className="p-4"><div className="flex items-center gap-2 text-red-400 mb-2"><AlertTriangle className="h-4 w-4" /><span className="text-sm font-medium">Production Mismatch Detected</span></div>
                      <p className="text-xs text-muted-foreground mb-2">These tests passed in pre-release but failed in post-release:</p>
                      {dashboard.mismatches.map(m => <Badge key={m.id} variant="outline" className="mr-1 mb-1 text-red-400">{m.title}</Badge>)}
                    </CardContent></Card>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Bug Summary</CardTitle></CardHeader><CardContent>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div><p className="text-lg font-bold text-red-500">{dashboard.bugs.open}</p><p className="text-[10px] text-muted-foreground">Open</p></div>
                        <div><p className="text-lg font-bold text-amber-500">{dashboard.bugs.assigned}</p><p className="text-[10px] text-muted-foreground">Assigned</p></div>
                        <div><p className="text-lg font-bold text-emerald-500">{dashboard.bugs.closed}</p><p className="text-[10px] text-muted-foreground">Closed</p></div>
                      </div>
                    </CardContent></Card>
                    <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Sessions</CardTitle></CardHeader><CardContent>
                      <div className="space-y-2">{dashboard.session_stats.map(s => (
                        <div key={s.id} className="flex items-center justify-between text-sm">
                          <span className="truncate flex-1">{s.name}</span>
                          <div className="flex items-center gap-1"><Badge className="text-[10px] bg-emerald-500/10 text-emerald-500">{s.passed}P</Badge><Badge className="text-[10px] bg-red-500/10 text-red-500">{s.failed}F</Badge></div>
                        </div>
                      ))}</div>
                    </CardContent></Card>
                  </div>
                </>
              ) : <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>}
            </TabsContent>

            {/* ===== TEST CASES ===== */}
            <TabsContent value="test-cases" className="space-y-4">
              <div className="flex justify-between"><div /><Button onClick={() => { setEditCaseId(null); setCaseForm({ title: '', module: '', steps: '', expected_result: '', priority: 'medium', is_critical: false }); setCaseDialog(true); }} data-testid="add-test-case-btn"><Plus className="h-4 w-4 mr-2" />Add Test Case</Button></div>
              {testCases.length === 0 ? <Card><CardContent className="py-12 text-center text-muted-foreground"><FlaskConical className="h-10 w-10 mx-auto mb-3 opacity-40" /><p>No test cases yet</p></CardContent></Card> : (
                <Card><Table><TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Module</TableHead><TableHead>Priority</TableHead><TableHead>Critical</TableHead><TableHead className="w-[80px]">Actions</TableHead></TableRow></TableHeader>
                  <TableBody>{testCases.map(tc => (
                    <TableRow key={tc.id} data-testid={`tc-${tc.id}`}>
                      <TableCell className="font-medium">{tc.title}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{tc.module || '-'}</Badge></TableCell>
                      <TableCell><Badge className={priorityColors[tc.priority]}>{tc.priority}</Badge></TableCell>
                      <TableCell>{tc.is_critical ? <Badge className="bg-red-500/20 text-red-400">Critical</Badge> : '-'}</TableCell>
                      <TableCell><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditCaseId(tc.id); setCaseForm({ title: tc.title, module: tc.module || '', steps: tc.steps || '', expected_result: tc.expected_result || '', priority: tc.priority, is_critical: tc.is_critical }); setCaseDialog(true); }}><Edit className="h-3.5 w-3.5" /></Button></TableCell>
                    </TableRow>
                  ))}</TableBody></Table></Card>
              )}
            </TabsContent>

            {/* ===== SESSIONS ===== */}
            <TabsContent value="sessions" className="space-y-4">
              {sessionDetailId && sessionDetail ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Button variant="ghost" size="icon" onClick={() => { setSessionDetailId(null); setSessionDetail(null); }}><ArrowLeft className="h-4 w-4" /></Button>
                      <div><h3 className="font-semibold">{sessionDetail.name}</h3><p className="text-xs text-muted-foreground">{sessionDetail.session_type.replace('_', '-')} | {sessionDetail.build_version || 'No version'}</p></div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => { setChecklistForm({ title: '', items: '' }); setChecklistDialog(true); }}><ClipboardCheck className="h-3.5 w-3.5 mr-1" />Checklist</Button>
                      <Button size="sm" onClick={() => { setSelectedCaseIds([]); fetchTestCases(); setAddCasesDialog(true); }} data-testid="add-cases-btn"><Plus className="h-3.5 w-3.5 mr-1" />Add Cases</Button>
                    </div>
                  </div>
                  {/* Execution Table */}
                  {sessionDetail.execution_cases?.length > 0 && (
                    <Card><Table><TableHeader><TableRow><TableHead>Test Case</TableHead><TableHead>Module</TableHead><TableHead>Status</TableHead><TableHead className="w-[200px]">Actions</TableHead></TableRow></TableHeader>
                      <TableBody>{sessionDetail.execution_cases.map(ec => (
                        <TableRow key={ec.id}>
                          <TableCell className="font-medium text-sm">{ec.test_case?.title || ec.test_case_id}</TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{ec.test_case?.module || '-'}</Badge></TableCell>
                          <TableCell><Badge className={statusColors[ec.status]}>{ec.status.replace('_', ' ')}</Badge></TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {['pass', 'fail', 'blocked'].map(s => (
                                <Button key={s} size="sm" variant={ec.status === s ? 'default' : 'outline'} className={`h-7 text-xs ${s === 'pass' ? 'text-emerald-500' : s === 'fail' ? 'text-red-500' : 'text-amber-500'}`} onClick={() => handleExecStatus(ec.id, s)} data-testid={`exec-${s}-${ec.id}`}>
                                  {s === 'pass' ? <CheckCircle2 className="h-3 w-3" /> : s === 'fail' ? <XCircle className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                                </Button>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}</TableBody></Table></Card>
                  )}
                  {/* Checklists */}
                  {sessionDetail.checklists?.length > 0 && (
                    <div className="space-y-3">{sessionDetail.checklists.map(cl => (
                      <Card key={cl.id}><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><ClipboardCheck className="h-4 w-4" />{cl.title}</CardTitle></CardHeader>
                        <CardContent className="space-y-1">{(cl.items || []).map(item => (
                          <div key={item.id} className="flex items-center justify-between py-1 border-b border-border/30 last:border-0">
                            <span className={`text-sm ${item.status === 'pass' ? 'line-through text-muted-foreground' : ''}`}>{item.label}</span>
                            <div className="flex gap-1">
                              <Button size="sm" variant={item.status === 'pass' ? 'default' : 'ghost'} className="h-6 w-6 p-0 text-emerald-500" onClick={() => handleChecklistItemStatus(item.id, 'pass')}><CheckCircle2 className="h-3 w-3" /></Button>
                              <Button size="sm" variant={item.status === 'fail' ? 'default' : 'ghost'} className="h-6 w-6 p-0 text-red-500" onClick={() => handleChecklistItemStatus(item.id, 'fail')}><XCircle className="h-3 w-3" /></Button>
                            </div>
                          </div>
                        ))}</CardContent></Card>
                    ))}</div>
                  )}
                </div>
              ) : (
                <>
                  <div className="flex justify-between"><div /><Button onClick={() => setSessionDialog(true)} data-testid="create-session-btn"><Plus className="h-4 w-4 mr-2" />Create Session</Button></div>
                  {sessions.length === 0 ? <Card><CardContent className="py-12 text-center text-muted-foreground"><Play className="h-10 w-10 mx-auto mb-3 opacity-40" /><p>No test sessions yet</p></CardContent></Card> : (
                    <div className="space-y-3">{sessions.map(s => (
                      <Card key={s.id} className="cursor-pointer hover:border-primary/30 transition-colors" onClick={() => setSessionDetailId(s.id)} data-testid={`session-${s.id}`}>
                        <CardContent className="p-4 flex items-center justify-between">
                          <div>
                            <p className="font-medium">{s.name}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">{s.session_type.replace('_', '-')}</Badge>
                              {s.build_version && <span className="text-xs text-muted-foreground">v{s.build_version}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {s.total_cases > 0 && <>
                              <Badge className="bg-emerald-500/10 text-emerald-500 text-xs">{s.passed}P</Badge>
                              <Badge className="bg-red-500/10 text-red-500 text-xs">{s.failed}F</Badge>
                              <Badge className="bg-zinc-500/10 text-zinc-400 text-xs">{s.not_run}NR</Badge>
                            </>}
                          </div>
                        </CardContent>
                      </Card>
                    ))}</div>
                  )}
                </>
              )}
            </TabsContent>

            {/* ===== BUGS ===== */}
            <TabsContent value="bugs" className="space-y-4">
              <div className="flex justify-between"><div /><Button onClick={() => setBugDialog(true)} className="text-red-500 border-red-500/30" variant="outline" data-testid="add-bug-btn"><Bug className="h-4 w-4 mr-2" />Report Bug</Button></div>
              {bugs.length === 0 ? <Card><CardContent className="py-12 text-center text-muted-foreground"><Bug className="h-10 w-10 mx-auto mb-3 opacity-40" /><p>No bugs reported</p></CardContent></Card> : (
                <Card><Table><TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Priority</TableHead><TableHead>Status</TableHead><TableHead>Reporter</TableHead><TableHead>Found In</TableHead><TableHead className="w-[120px]">Actions</TableHead></TableRow></TableHeader>
                  <TableBody>{bugs.map(b => (
                    <TableRow key={b.id} data-testid={`bug-${b.id}`}>
                      <TableCell className="font-medium text-sm">{b.title}</TableCell>
                      <TableCell><Badge className={priorityColors[b.priority]}>{b.priority}</Badge></TableCell>
                      <TableCell><Badge className={bugStatusColors[b.status]}>{b.status}</Badge></TableCell>
                      <TableCell className="text-sm text-muted-foreground">{b.reported_by_name || '-'}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{b.found_in ? b.found_in.replace('_', '-') : '-'}</Badge></TableCell>
                      <TableCell>
                        <Select value={b.status} onValueChange={v => handleBugStatus(b.id, v)}>
                          <SelectTrigger className="h-7 text-xs w-[100px]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="assigned">Assigned</SelectItem>
                            <SelectItem value="fixed">Fixed</SelectItem>
                            <SelectItem value="retest">Retest</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}</TableBody></Table></Card>
              )}
            </TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>

      {/* ===== DIALOGS ===== */}
      {/* Test Case Dialog */}
      <Dialog open={caseDialog} onOpenChange={setCaseDialog}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>{editCaseId ? 'Edit' : 'Add'} Test Case</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Title *</Label><Input value={caseForm.title} onChange={e => setCaseForm(f => ({...f, title: e.target.value}))} data-testid="tc-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Module</Label><Input value={caseForm.module} onChange={e => setCaseForm(f => ({...f, module: e.target.value}))} placeholder="e.g. Auth, Dashboard" data-testid="tc-module" /></div>
              <div className="space-y-1"><Label>Priority</Label><Select value={caseForm.priority} onValueChange={v => setCaseForm(f => ({...f, priority: v}))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent></Select></div>
            </div>
            <div className="space-y-1"><Label>Steps</Label><Textarea value={caseForm.steps} onChange={e => setCaseForm(f => ({...f, steps: e.target.value}))} rows={3} placeholder="Step-by-step instructions..." data-testid="tc-steps" /></div>
            <div className="space-y-1"><Label>Expected Result</Label><Textarea value={caseForm.expected_result} onChange={e => setCaseForm(f => ({...f, expected_result: e.target.value}))} rows={2} data-testid="tc-expected" /></div>
            <div className="flex items-center gap-2"><Checkbox checked={caseForm.is_critical} onCheckedChange={c => setCaseForm(f => ({...f, is_critical: c}))} data-testid="tc-critical" /><Label>Mark as Critical</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCaseDialog(false)}>Cancel</Button><Button onClick={handleSaveCase} data-testid="tc-save">{editCaseId ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Session Dialog */}
      <Dialog open={sessionDialog} onOpenChange={setSessionDialog}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Create Test Session</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Session Name *</Label><Input value={sessionForm.name} onChange={e => setSessionForm(f => ({...f, name: e.target.value}))} placeholder="e.g. Sprint 5 Pre-release" data-testid="session-name" /></div>
            <div className="space-y-1"><Label>Session Type *</Label><Select value={sessionForm.session_type} onValueChange={v => setSessionForm(f => ({...f, session_type: v}))}><SelectTrigger data-testid="session-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="pre_release">Pre-Release</SelectItem><SelectItem value="post_release">Post-Release</SelectItem></SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Build Version</Label><Input value={sessionForm.build_version} onChange={e => setSessionForm(f => ({...f, build_version: e.target.value}))} placeholder="e.g. 2.1.0" data-testid="session-version" /></div>
              <div className="space-y-1"><Label>Build Source</Label><Input value={sessionForm.build_source} onChange={e => setSessionForm(f => ({...f, build_source: e.target.value}))} placeholder="e.g. APK, Web" data-testid="session-source" /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setSessionDialog(false)}>Cancel</Button><Button onClick={handleSaveSession} data-testid="session-save">Create Session</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bug Dialog */}
      <Dialog open={bugDialog} onOpenChange={setBugDialog}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle className="flex items-center gap-2"><Bug className="h-5 w-5 text-red-500" />Report Bug</DialogTitle><DialogDescription>Report an issue found during testing</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Title *</Label><Input value={bugForm.title} onChange={e => setBugForm(f => ({...f, title: e.target.value}))} placeholder="Brief description of the bug" data-testid="bug-title" /></div>
            <div className="space-y-1"><Label>Description</Label><Textarea value={bugForm.description} onChange={e => setBugForm(f => ({...f, description: e.target.value}))} rows={3} placeholder="Steps to reproduce, expected vs actual..." data-testid="bug-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Priority</Label><Select value={bugForm.priority} onValueChange={v => setBugForm(f => ({...f, priority: v}))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent></Select></div>
              <div className="space-y-1"><Label>Found In</Label><Select value={bugForm.found_in} onValueChange={v => setBugForm(f => ({...f, found_in: v}))}><SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent><SelectItem value="pre_release">Pre-Release</SelectItem><SelectItem value="post_release">Post-Release</SelectItem></SelectContent></Select></div>
            </div>
            <div className="space-y-1"><Label>Assign To</Label><Select value={bugForm.assigned_to} onValueChange={v => setBugForm(f => ({...f, assigned_to: v}))}><SelectTrigger><SelectValue placeholder="Select developer..." /></SelectTrigger><SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setBugDialog(false)}>Cancel</Button><Button onClick={handleSaveBug} className="bg-red-500 hover:bg-red-600" data-testid="bug-save">Report Bug</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Cases to Session Dialog */}
      <Dialog open={addCasesDialog} onOpenChange={setAddCasesDialog}>
        <DialogContent className="max-w-lg max-h-[70vh] overflow-y-auto"><DialogHeader><DialogTitle>Add Test Cases to Session</DialogTitle></DialogHeader>
          <div className="space-y-2">{testCases.map(tc => (
            <label key={tc.id} className="flex items-center gap-3 p-2 rounded hover:bg-muted/20 cursor-pointer">
              <Checkbox checked={selectedCaseIds.includes(tc.id)} onCheckedChange={c => setSelectedCaseIds(prev => c ? [...prev, tc.id] : prev.filter(x => x !== tc.id))} />
              <span className="text-sm flex-1">{tc.title}</span>
              <Badge variant="outline" className="text-xs">{tc.module || '-'}</Badge>
            </label>
          ))}</div>
          <DialogFooter><Button variant="outline" onClick={() => setAddCasesDialog(false)}>Cancel</Button><Button onClick={handleAddCasesToSession} data-testid="confirm-add-cases">Add {selectedCaseIds.length} Cases</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Checklist Dialog */}
      <Dialog open={checklistDialog} onOpenChange={setChecklistDialog}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Create Checklist</DialogTitle><DialogDescription>Quick checklist for smoke/validation testing</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Title *</Label><Input value={checklistForm.title} onChange={e => setChecklistForm(f => ({...f, title: e.target.value}))} placeholder="e.g. Post-deploy Smoke Test" data-testid="cl-title" /></div>
            <div className="space-y-1"><Label>Items (one per line)</Label><Textarea value={checklistForm.items} onChange={e => setChecklistForm(f => ({...f, items: e.target.value}))} rows={5} placeholder="Login works&#10;Dashboard loads&#10;Payments functional" data-testid="cl-items" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setChecklistDialog(false)}>Cancel</Button><Button onClick={handleSaveChecklist} data-testid="cl-save">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Members Dialog */}
      <Dialog open={addMemberDialog} onOpenChange={setAddMemberDialog}>
        <DialogContent className="max-w-lg max-h-[70vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Manage Team Members</DialogTitle><DialogDescription>Currently assigned members are shown checked. Tick more users to add them.</DialogDescription></DialogHeader>
          {users.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No users available</p>
          ) : (
            <div className="space-y-1">{users.map(u => {
              const isMember = memberIds.has(u.id);
              const isSelected = selectedMemberIds.includes(u.id);
              return (
                <label key={u.id} className={`flex items-center gap-3 p-2 rounded ${isMember ? 'bg-emerald-500/5' : 'hover:bg-muted/20 cursor-pointer'}`} data-testid={`member-row-${u.id}`}>
                  <Checkbox
                    checked={isMember || isSelected}
                    disabled={isMember}
                    onCheckedChange={c => setSelectedMemberIds(prev => c ? [...prev, u.id] : prev.filter(x => x !== u.id))}
                  />
                  <div className="flex items-center gap-2 flex-1">
                    <PersonAvatar firstName={u.first_name} lastName={u.last_name} picture={u.profile_picture} seed={u.id} size="sm" />
                    <div className="flex-1">
                      <span className="text-sm font-medium">{u.first_name} {u.last_name}</span>
                      <span className="text-xs text-muted-foreground ml-2">{u.email}</span>
                    </div>
                    {isMember && <Badge className="bg-emerald-500/10 text-emerald-500 text-[10px]">Already a member</Badge>}
                  </div>
                </label>
              );
            })}</div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setAddMemberDialog(false)}>Cancel</Button><Button onClick={handleAddMembers} disabled={!selectedMemberIds.length} data-testid="confirm-add-members">Add {selectedMemberIds.length} Member{selectedMemberIds.length !== 1 ? 's' : ''}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
